import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  getNextPortalLanguage,
  resolveInitialPortalLanguage,
} from './portalLanguage';

const LANG_KEY = 'aiker_lang';
const LANGUAGE_CHANGE_EVENT = 'aiker-language-change';

const translations = {
  en: {
    nav_group_ai_knowledge: 'Reception setup',
    nav_group_user_management: 'Calls & operations',
    nav_settings: 'Company Info & AI Settings',
    nav_extensions: 'Seats',
    nav_contacts: 'Contacts',
    nav_call_history: 'Call History',
    nav_chat_records: 'Website Chat',
    nav_callback_requests: 'Callbacks & follow-up',
    nav_customers: 'Customers',
    nav_calendar: 'Calendar',
    nav_products: 'Product Info',
    nav_qa: 'QA Knowledge Base',
    nav_qa_training: 'QA Training',
    nav_ai_test: 'AI Test',
    nav_voicemail: 'Voicemail',
    nav_sms_logs: 'SMS',
    sign_out: 'Sign Out',
    enterprise_plan: 'Enterprise Plan',
    lang_label: '中文',
    tenant_user: 'Tenant User',
    expand_sidebar: 'Expand sidebar',
    collapse_sidebar: 'Collapse sidebar',
    open_menu: 'Open menu',
    login_title: 'Bizsprite Console',
    login_subtitle: 'Sign in to your account',
    login_username: 'Username',
    login_password: 'Password',
    login_username_placeholder: 'email@company.com',
    login_show_password: 'Show password',
    login_hide_password: 'Hide password',
    login_submit: 'Sign In',
    login_submitting: 'Signing in…',
    login_terms_prefix: 'By signing in, you agree to our',
    login_terms: 'Terms of Service',
    login_and: 'and',
    login_privacy: 'Privacy Policy',
  },
  zh: {
    nav_group_ai_knowledge: '接待设置',
    nav_group_user_management: '通话与跟进',
    nav_settings: '公司信息 & AI 设置',
    nav_extensions: '席位',
    nav_contacts: '联系人',
    nav_call_history: '通话记录',
    nav_chat_records: '网页客服记录',
    nav_callback_requests: '回电与待跟进',
    nav_customers: '客户',
    nav_calendar: '日历',
    nav_products: '产品信息',
    nav_qa: 'QA 知识库',
    nav_qa_training: 'QA 训练中心',
    nav_ai_test: 'AI 测试',
    nav_voicemail: '语音信箱',
    nav_sms_logs: '短信',
    sign_out: '退出登录',
    enterprise_plan: '企业版',
    lang_label: 'English',
    tenant_user: '租户用户',
    expand_sidebar: '展开侧边栏',
    collapse_sidebar: '收起侧边栏',
    open_menu: '打开菜单',
    login_title: 'Bizsprite Console',
    login_subtitle: '登录您的账户',
    login_username: '用户名',
    login_password: '密码',
    login_username_placeholder: 'email@company.com',
    login_show_password: '显示密码',
    login_hide_password: '隐藏密码',
    login_submit: '登录',
    login_submitting: '登录中…',
    login_terms_prefix: '登录即表示您同意我们的',
    login_terms: '服务条款',
    login_and: '和',
    login_privacy: '隐私政策',
  },
};

const LanguageContext = createContext(null);

export const switchLang = () => {
  const current = localStorage.getItem(LANG_KEY) || 'en';
  const next = getNextPortalLanguage(current);
  localStorage.setItem(LANG_KEY, next);
  window.dispatchEvent(new CustomEvent(LANGUAGE_CHANGE_EVENT, { detail: next }));
  return next;
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => resolveInitialPortalLanguage({
    search: window.location.search,
    stored: localStorage.getItem(LANG_KEY),
  }));

  useEffect(() => {
    localStorage.setItem(LANG_KEY, lang);
    // The public site sets <html lang> from its own URL (pages/siteHead.js).
    if (!document.documentElement.classList.contains('site-page')) {
      document.documentElement.lang = lang === 'zh' ? 'zh-CN' : 'en';
    }
  }, [lang]);

  useEffect(() => {
    const handleLanguageChange = (event) => setLang(event.detail);
    window.addEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
    return () => window.removeEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
  }, []);

  const t = (key) => translations[lang]?.[key] ?? key;

  return (
    <LanguageContext.Provider value={{ lang, t, setLang }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
};

// For shared components that may render outside the provider (tests, isolated widgets).
export const useOptionalLanguage = () => useContext(LanguageContext);
